import clsx from "clsx";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { usePaymentContext } from "@/contexts/PaymentContext";
import type {
  FlightType,
  MappedFlightOffer,
} from "@/features/flights/types/types";
import { extractAirlineCarriers } from "@/features/flights/utils/extractAirlineCarriers";
import { calculateMonthlyInstallment, includeBnplFee } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

import { BaggageIncluded } from "./flight-list/BaggageIncluded";
import { SingleAvailableFlight } from "./SingleAvailableFlight";
import { SingleFlightInfo } from "./SingleFlightInfo";

type Props = {
  flight: FlightType;
  offer: MappedFlightOffer;
  direction: "departure" | "return";
  onClick: () => void;
};

export const SingleAvailableFlightV2 = ({
  flight,
  offer,
  direction,
  onClick,
}: Props) => {
  const intl = useIntl();
  const allAirlines = extractAirlineCarriers(flight.segments);

  const isTwoWaySearch = !!offer.flightsOverview[0].returnFlight;

  const firstSegment = flight.segments[0];
  const lastSegment = flight.segments[flight.segments.length - 1];
  const doesOperatingCarrierDiffer =
    firstSegment.operatingCarrier.airline.code !==
    firstSegment.marketingCarrier.airline.code;

  const { maxAvailableInstallments, isEligible } = usePaymentContext();
  const isBnplFeatureEnabled = useFeatureIsOn("bnpl") && Boolean(isEligible);
  const includedBnplFee = includeBnplFee(offer.totalPrice);
  const monthlyPayment = calculateMonthlyInstallment(
    includedBnplFee,
    maxAvailableInstallments,
  );

  const isBaggageIncluded = offer.baggageAllowanceList.some((list) =>
    list.baggageAllowance.some((allowance) => Number(allowance.quantity) > 0),
  );

  return (
    <SingleAvailableFlight
      onClick={onClick}
      airlines={allAirlines}
      topInfo={isBaggageIncluded ? <BaggageIncluded /> : null}
      bottomInfo={
        <div className="flex w-full justify-between gap-2">
          {isBnplFeatureEnabled ? (
            <div
              className={clsx("flex grow items-start justify-between", {
                "flex-col": isBnplFeatureEnabled,
              })}
            >
              <div className="text-xs text-text-tertiary">
                {intl.formatMessage({
                  id: "payment.payMonthly",
                  defaultMessage: "Pay monthly",
                })}
              </div>
              <span className="font-bold text-text-primary" dir="ltr">
                {direction === "return"
                  ? "+0 IQD"
                  : formatCurrency(monthlyPayment, offer.currency)}
              </span>
            </div>
          ) : null}
          <div
            className={clsx("flex grow items-end justify-between", {
              "flex-col": isBnplFeatureEnabled,
            })}
          >
            <div className="text-xs text-text-tertiary">
              {isTwoWaySearch
                ? intl.formatMessage({
                    id: "payment.totalRoundTripPrice",
                    defaultMessage: "Total round trip",
                  })
                : intl.formatMessage({
                    id: "payment.standard",
                    defaultMessage: "Standard price",
                  })}
            </div>
            <span className="font-bold text-text-primary" dir="ltr">
              {direction === "return"
                ? "+0 IQD"
                : formatCurrency(offer.totalPrice, offer.currency)}
            </span>
          </div>
        </div>
      }
    >
      <SingleFlightInfo
        origin={{
          date: firstSegment.departure.date,
          time: firstSegment.departure.time,
          code: firstSegment.departure.airport.code,
        }}
        destination={{
          date: lastSegment.arrival.date,
          time: lastSegment.arrival.time,
          code: lastSegment.arrival.airport.code,
        }}
        duration={flight.duration}
        stops={flight.stops?.length || 0}
        operatedBy={
          doesOperatingCarrierDiffer
            ? firstSegment.operatingCarrier.airline.name ||
              firstSegment.operatingCarrier.airline.code
            : undefined
        }
      />
    </SingleAvailableFlight>
  );
};
